import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import musicController from "../../audio/music-controller";
import GoldenEggIcon from "../../shared/GoldenEggIcon";
import { eggBalanceNow, refreshEggBalance, setEggBalance } from "../../shared/shop/useEggBalance";
import { openRoyaleReward, type CrownReward } from "../../shared/royale";
import RewardArt, { rewardTitle } from "../play/dynamic/RewardArt";
import { runEggCelebration } from "./purchase-celebration";
import s from "./RoyaleClaim.module.scss";

// Claiming a Royale win's reward, in the Store (the win screen shows what was
// won and sends you here):
//   ClaimStrip   a gold strip at the top of the Store for each reward waiting
//   ClaimReveal  the claim itself, over the whole screen: the reward rises out
//                of the light, its eggs fly to your count, and a won head can
//                be worn straight away
// `demo` (the design lab): no server, the eggs counted where the count is.

const Eggs = ({ n, size = 14 }: { n: number; size?: number }) => <span className={s.eggs}>+{n}<GoldenEggIcon size={size} /></span>;

export function ClaimStrip({ r, onClaim }: { r: CrownReward; onClaim: () => void }) {
  return (
    <div className={s.strip}>
      <RewardArt r={r} className={s.stripArt} />
      <span className={s.text}><small>Royale reward</small><b>{rewardTitle(r)}</b><Eggs n={r.eggs} /></span>
      <button type="button" className={`${s.btn} ${s.btnGold}`} onClick={onClaim} data-sound="none">Claim</button>
    </div>
  );
}

export function ClaimReveal({ r, onClaimed, onClose, onWear, demo }: {
  r: CrownReward;
  onClaimed?: (got: { eggs: number; item: { kind: string; id: string } | null }) => void;
  onClose: () => void;
  onWear?: () => void;        // a head: wear it now
  demo?: boolean;
}) {
  const [phase, setPhase] = useState<"rising" | "shown" | "failed">("rising");
  const eggRef = useRef<HTMLSpanElement | null>(null);
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    musicController.play("unbox");
    const before = eggBalanceNow();
    (async () => {
      const [res] = await Promise.all([
        demo ? Promise.resolve({ eggs: r.eggs, item: r.item || null, balance: null as number | null, error: undefined as string | undefined }) : openRoyaleReward(r.id, true),
        new Promise((ok) => setTimeout(ok, 900)), // it rises, whatever the answer's speed
      ]);
      if (res.error) { setPhase("failed"); refreshEggBalance(); return; }
      setPhase("shown");
      musicController.play("rank-up");
      const eggs = res.eggs ?? r.eggs;
      onClaimed?.({ eggs, item: res.item || null });
      if (eggs > 0 && before != null && eggRef.current) {
        await runEggCelebration({
          slot: null, total: eggs, notes: true,
          sources: [{ el: eggRef.current, count: eggs }],
          onProgress: (landed) => setEggBalance(before + landed),
        });
      }
      if (typeof res.balance === "number") setEggBalance(res.balance); else if (!demo) refreshEggBalance();
    })();
  }, []);

  const head = !!(r.item && r.item.kind === "head");
  return createPortal(
    <div className={s.reveal} role="dialog" aria-label={`Royale reward: ${rewardTitle(r)}`}>
      <div className={s.rays} aria-hidden="true" />
      <div className={s.inner}>
        <span className={s.kicker}>Royale reward</span>
        <RewardArt r={r} className={s.art} />
        <h2 className={s.title}>{rewardTitle(r)}</h2>
        {phase === "failed"
          ? <p className={s.line}>Not claimed. Try again in a moment.</p>
          : <span className={s.got}><span ref={eggRef} className={s.gotEgg}><GoldenEggIcon size={22} /></span>+{r.eggs} Golden Eggs</span>}
        <div className={s.btns}>
          {phase === "shown" && head && onWear
            ? (<><button type="button" className={s.btn} onClick={onClose}>Later</button><button type="button" className={`${s.btn} ${s.btnGold}`} onClick={onWear}>Wear it</button></>)
            : <button type="button" className={`${s.btn} ${phase === "shown" ? s.btnGold : ""}`} onClick={onClose} disabled={phase === "rising"}>{phase === "failed" ? "Close" : "Done"}</button>}
        </div>
      </div>
    </div>,
    document.body,
  );
}
